import { memo, useMemo } from 'react'
import { atom } from 'jotai'
import { styled } from 'styled-components'
import { Currency, TradeType } from '@pancakeswap/sdk'
import { Route, SmartRouter, SmartRouterTrade } from '@pancakeswap/smart-router/evm'
import { Flex, Text } from '@pancakeswap/uikit'
import { CurrencyLogo } from 'components/Logo'
import { v3FeeToPercent } from '../utils/exchange'

// current quoted trade, shared so the route panel can render outside the swap card.
// initialized via a value cast so jotai infers a writable PrimitiveAtom (a bare
// `atom<T | null>(null)` was resolving to a read-only Atom under these generics).
export const routeTradeAtom = atom(null as SmartRouterTrade<TradeType> | null)

const Panel = styled.div`
  margin-top: 20px;
  width: 100%;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  background: rgba(250, 250, 250, 0.03);
  padding: 16px;
`

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.cardBorder};
  margin: 12px 0;
`

const PercentChip = styled.div`
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.input};
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`

const HopChip = styled.div`
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  background: ${({ theme }) => theme.colors.input};
  overflow: hidden;
  text-align: center;
  flex: 1;
  min-width: 104px;
  & > div:first-child {
    padding: 6px 10px;
    font-size: 13px;
    font-weight: 600;
    background: rgba(250, 250, 250, 0.05);
    border-bottom: 1px solid ${({ theme }) => theme.colors.cardBorder};
    white-space: nowrap;
  }
  & > div:last-child {
    padding: 5px 10px;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.textSubtle};
    white-space: nowrap;
  }
`

const RouteRow = styled(Flex)`
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

type Pair = [Currency, Currency]

function poolLabel(pool: Route['pools'][number]) {
  if (SmartRouter.isV2Pool(pool)) return 'V2'
  if (SmartRouter.isV3Pool(pool)) return `V3 · ${v3FeeToPercent(pool.fee).toSignificant(3)}%`
  return 'Stable'
}

const RouteLine = memo(function RouteLine({ route }: { route: Route }) {
  const { path, pools, inputAmount, outputAmount, percent } = route

  const pairs = useMemo<Pair[]>(() => {
    if (path.length <= 1) return []
    const currencyPairs: Pair[] = []
    for (let i = 0; i < path.length - 1; i += 1) {
      currencyPairs.push([path[i], path[i + 1]])
    }
    return currencyPairs
  }, [path])

  return (
    <RouteRow>
      <CurrencyLogo currency={inputAmount.currency} size="28px" />
      <PercentChip>{percent}%</PercentChip>
      {pairs.map(([input, output], i) => (
        // eslint-disable-next-line react/no-array-index-key
        <HopChip key={`${input.symbol}-${output.symbol}-${i}`}>
          <div>
            {input.symbol} → {output.symbol}
          </div>
          <div>{poolLabel(pools[i])}</div>
        </HopChip>
      ))}
      <CurrencyLogo currency={outputAmount.currency} size="28px" />
    </RouteRow>
  )
})

const PathLine = memo(function PathLine({ path }: { path: Currency[] }) {
  const pairs = useMemo<Pair[]>(() => {
    if (path.length <= 1) return []
    const currencyPairs: Pair[] = []
    for (let i = 0; i < path.length - 1; i += 1) {
      currencyPairs.push([path[i], path[i + 1]])
    }
    return currencyPairs
  }, [path])

  return (
    <RouteRow>
      <CurrencyLogo currency={path[0]} size="28px" />
      <PercentChip>100%</PercentChip>
      {pairs.map(([input, output], i) => (
        // eslint-disable-next-line react/no-array-index-key
        <HopChip key={`${input.symbol}-${output.symbol}-${i}`}>
          <div>
            {input.symbol} → {output.symbol}
          </div>
          <div>V2</div>
        </HopChip>
      ))}
      <CurrencyLogo currency={path[path.length - 1]} size="28px" />
    </RouteRow>
  )
})

interface Props {
  trade?: SmartRouterTrade<TradeType> | null
  path?: Currency[]
  showPlaceholder?: boolean
}

export const RouteDetailsPanel = memo(function RouteDetailsPanel({ trade, path, showPlaceholder }: Props) {
  const routes = trade?.routes
  const hasRoutes = Boolean(routes?.length)
  const hasPath = Boolean(path && path.length > 1)

  if (!hasRoutes && !hasPath) {
    if (!showPlaceholder) {
      return null
    }
    return (
      <Panel>
        <Text fontSize="15px" bold>
          Route Details
        </Text>
        <Divider />
        <Text fontSize="13px" color="textSubtle">
          Select tokens and enter an amount — the best route for your trade will appear here.
        </Text>
      </Panel>
    )
  }

  return (
    <Panel>
      <Text fontSize="15px" bold>
        Route Details
      </Text>
      <Divider />
      <Flex flexDirection="column" style={{ gap: '12px' }}>
        {hasRoutes
          ? routes.map((route, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <RouteLine key={i} route={route} />
            ))
          : path && <PathLine path={path} />}
      </Flex>
      <Divider />
      <Text fontSize="12px" color="textSubtle">
        Routes refresh automatically to find the best price for your trade.
      </Text>
    </Panel>
  )
})
